import { Add, Edit, Delete } from '@mui/icons-material';
import { blue, red } from '@mui/material/colors';
import { GridRowParams, GridActionsCellItem, DataGrid } from '@mui/x-data-grid';
import ProjectQueries from './ProjectQueries';
import { Button } from '@mui/material';
import { useState } from 'react';
import { ClientDto, ProjectDto } from '../../../../api';
import ConfirmDialog, { IConfirmDialog } from '../../../../components/form/controls/ConfirmDialog';
import Popup from '../../../../components/Popup';
import useDataGridStyles from '../../../../styles/useDataGridStyles';
import { renderCellExpand } from '../../../../components/datagrid/mui/renderCellExpand';
import ProjectForm from './ProjectForm';
import CreateProjectPopup from '../../../../components/form/popups/CreateProjectPopup';
import { LightToolTip } from '../../../../components/form/react-hook-form-components/ToolTipTheme';

interface IProjectsDataGridProps {
  selectedClient?: ClientDto;
  projects?: ProjectDto[];
}

/**
 * Generates a Data Grid filled with Projects
 */
function ProjectsDataGrid(props: IProjectsDataGridProps) {
  const classes = useDataGridStyles();
  const { selectedClient, projects } = props;
  const { mutateUpdateProject, mutateDeleteProject } = ProjectQueries();
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isUpdateFormOpen, setIsUpdateFormOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectDto | undefined>(undefined);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    title: '',
    content: '',
  } as IConfirmDialog);

  /**
   * The Data Grid column headings
   */
  const columns = [
    {
      field: 'clientName',
      headerName: 'Client',
      editable: false,
      hide: selectedClient !== undefined,
      renderCellExpand: renderCellExpand,
      flex: 1,
    },
    {
      field: 'name',
      headerName: 'Project',
      renderCell: renderCellExpand,
      flex: 1,
    },
    {
      field: 'description',
      headerName: 'Description',
      editable: false,
      renderCell: renderCellExpand,
      flex: 2,
    },
    {
      field: 'isBillable',
      headerName: 'Billable',
      editable: false,
      flex: 0.5,
    },
    {
      field: 'isActive',
      headerName: 'Active',
      minWidth: 80,
      editable: false,
      flex: 0.5,
    },
    {
      field: 'kCode',
      headerName: 'K Code',
      minWidth: 50,
      editable: false,
      flex: 0.5,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      type: 'actions',
      sortable: false,
      minWidth: 80,
      flex: 0.3,
      getActions: (params: GridRowParams) => {
        return [
          <GridActionsCellItem
            icon={
              <LightToolTip title="Edit" placement="bottom">
                <Edit sx={{ color: blue[500] }} />
              </LightToolTip>
            }
            label="Edit"
            onClick={() => {
              handleEditProject(params.id as number);
            }}
          />,
          <GridActionsCellItem
            icon={
              <LightToolTip title="Delete" placement="bottom">
                <Delete sx={{ color: red[900] }} />
              </LightToolTip>
            }
            label="Delete"
            onClick={() => handleDeleteProject(params.id as number)}
          />,
        ];
      },
    },
  ];

  /**
   * Click handler for the edit project button
   * @param id the id of the project to edit
   */
  const handleEditProject = (id: number) => {
    const project = projects?.find(team => team.id === id);
    if (project !== undefined) {
      setSelectedProject(project);
      setIsUpdateFormOpen(true);
    }
  };

  /**
   * Handler for deleting a Project
   * @param id the id of the Project to delete
   */
  const handleDeleteProject = (id: number) => {
    setDeleteDialog({
      isOpen: true,
      title: `Are you sure you want to delete this project?`,
      content: "You can't undo this operation",
      onConfirm: () => {
        mutateDeleteProject(id, {
          onSuccess: closeDeleteDialog,
        });
      },
    });
  };

  /**
   * Closes the delete dialog
   */
  const closeDeleteDialog = () => {
    setDeleteDialog({
      ...deleteDialog,
      isOpen: false,
    });
  };

  return (
    <div>
      <h3>{selectedClient ? selectedClient.name : 'All'} Projects</h3>
      <Button startIcon={<Add />} onClick={() => setIsCreateFormOpen(true)}>
        Add Project
      </Button>
      <DataGrid
        className={classes.root}
        autoHeight
        columns={columns}
        rows={projects ?? []}
        disableSelectionOnClick={true}
      />
      <ConfirmDialog confirmDialog={deleteDialog} setConfirmDialog={setDeleteDialog} />

      <CreateProjectPopup isOpen={isCreateFormOpen} setIsOpen={setIsCreateFormOpen} client={selectedClient} />

      <Popup openPopup={isUpdateFormOpen} setOpenPopup={setIsUpdateFormOpen} title="Update Project">
        <ProjectForm
          onSubmit={values => {
            mutateUpdateProject(values, {
              onSuccess: () => setIsUpdateFormOpen(false),
            });
          }}
          onCancel={() => setIsUpdateFormOpen(false)}
          project={selectedProject}
          client={selectedClient}
        />
      </Popup>
    </div>
  );
}

export default ProjectsDataGrid;
